import ExcelJS from 'exceljs'
import { readCellText } from '../../shared/utils/excelCellText'
import { createProcurementResultsBulk, updateItemStatus } from '../../shared/api/requestItems.api'
import {
  findProcurementRequestCandidate,
  uploadProcurementResponse,
} from '../../shared/api/procurementRequests.api'
import { confirm } from '../../shared/ui/confirmStore'
import type { SourceType, ProcurementQueueItem } from '../../shared/api/types'

const MATRIX_MAP: Record<string, SourceType> = {
  Склад: 'STOCK',
  Китай: 'CHINA',
  РФ: 'RF',
  Производители: 'MANUFACTURER',
  Стоки: 'OTHER',
}

// Порог предложения привязки файла без метки к открытому запросу: запрос
// должен накрывать больше 60% позиций файла, иначе это скорее «другая партия
// цен» того же поставщика, чем потерявший метку ответ на запрос.
const MATCH_THRESHOLD = 0.6

interface ParsedRow {
  requestItemId: string | null
  partNumber: string
  brand: string | null
  supplierName: string
  price: number
  currency: string
  note: string
}

// Минимально нужный набор полей. Очередь закупки передаёт полный
// ProcurementQueueItem (он удовлетворяет Pick), карточка заявки —
// адаптированный RequestItem. Остальные поля парсеру не нужны.
export type ParserBatchItem = Pick<ProcurementQueueItem, 'requestItemId' | 'partNumber' | 'matrix'>

export interface BatchResponseResult {
  created: number
  skippedOrphans: number
  // Сколько строк из Excel пропущено на парсе (не нашли подходящую позицию
  // в текущем списке items) — это локальная фильтрация, отдельно от orphan
  // на бэке. Информация для тоста «загружено X, пропущено Y».
  skippedNoMatch: number
  // Полные дубли (поставщик+цена+заметка), отсеянные бэком при повторной
  // загрузке того же файла. Показываем в тосте, чтобы закупщик понимал
  // почему «загружено меньше, чем строк в файле».
  skippedDuplicates: number
  // Строки с ценой 0 — поставщик ответил «нет позиции». Квота создаётся
  // технической (цена 0), позиция у закупщика → «Не найдено».
  notFound: number
  // Строки файла, чья позиция была удалена вместе с заявкой (BOM-строка
  // осиротела, requestItemId=null на бэке): квоту привязать некуда. Раньше
  // эти строки исчезали молча — теперь показываем в тосте. Только batch-путь.
  unmatched: number
  // Строки с заполненной, но непарсящейся ценой («12,х5», «—», отрицательная):
  // раньше тихо отбрасывались на парсе, закупщик не понимал, почему «загрузилось
  // меньше, чем в файле». Теперь возвращаем номер строки Excel + что было в
  // ячейке цены, чтобы показать в тосте и человек нашёл ошибку в файле.
  invalidRows: { row: number; value: string }[]
}

export async function parseBatchResponse(
  file: File,
  items: ParserBatchItem[],
): Promise<BatchResponseResult> {
  const wb = new ExcelJS.Workbook()
  const buffer = await file.arrayBuffer()
  await wb.xlsx.load(buffer)
  const ws = wb.worksheets[0]
  if (!ws) throw new Error('Пустой файл')

  // Если файл связан с конкретным ProcurementRequest — маркер записан в
  // wb.subject при генерации (см. generateProcurementRequest.ts) в формате
  // `procurement-request:STRONG-20260507-001`. Только этот маркер — fallback
  // на wb.title не делаем, иначе старые файлы (где title могли проставить
  // другие редакторы Excel) ошибочно попадут в новую ветку.
  const subject = String(wb.subject ?? '')
  const fileCodeMatch = subject.match(/^procurement-request:([\w-]+)$/)
  const linkedFileCode = fileCodeMatch ? fileCodeMatch[1] : ''

  const headerRow = ws.getRow(1)
  const colCount = headerRow.cellCount

  // Синхронизировано с generateProcurementRequest.ts:
  //   старый batch:   1-№, 2-ID, 3-КодКлиента, 4-Заявка, 5-Supplier, 6-PartNumber, 7-Brand, 8-Qty, 9-Price, 10-Note, 11-Supplier comment
  //   новый batch:    … 9-Price, 10-Currency, 11-Supplier comment, 12-Note (с 15.05.2026)
  //   non-batch:      1-№, 2-Supplier, 3-PartNumber, 4-Brand, 5-Qty, 6-Price, 7-Note, 8-Supplier comment
  // Supplier comment (ответ поставщика → quote.note) в обоих batch-форматах —
  // столбец 11, поэтому COL-карта не зависит от версии формата.
  const isBatch = colCount >= 11 && readCellText(headerRow.getCell(2)).includes('ID')

  const COL = isBatch
    ? { id: 2, supplier: 5, partNumber: 6, brand: 7, price: 9, comment: 11 }
    : { id: 0, supplier: 2, partNumber: 3, brand: 4, price: 6, comment: 8 }

  // Детектор нового формата: ищем столбец с заголовком «Currency». Если есть —
  // валюта берётся из ячейки. Если нет (старый файл, скачан до 15.05) —
  // валюта всегда CNY, как работало раньше. Это позволяет закупщикам
  // догружать ответы по старым файлам, отправленным поставщикам недели назад.
  let currencyCol = 0
  for (let i = 1; i <= colCount; i++) {
    if (readCellText(headerRow.getCell(i)).toLowerCase() === 'currency') {
      currencyCol = i
      break
    }
  }
  const isNewFormat = currencyCol > 0

  // Валюта из ячейки: EUR/USD/CNY любым регистром. Пусто или неизвестное
  // значение → CNY (юань по умолчанию).
  const readCurrency = (row: ExcelJS.Row): string => {
    if (!isNewFormat) return 'CNY'
    const raw = readCellText(row.getCell(currencyCol)).toUpperCase()
    return raw === 'EUR' || raw === 'USD' || raw === 'CNY' ? raw : 'CNY'
  }

  // Строки с заполненной, но непарсящейся ценой — копим для диагностики в
  // тосте. Общий накопитель для обоих путей парсинга (новый и старый).
  const invalidRows: { row: number; value: string }[] = []

  // Новый путь: файл — ответ на конкретный ProcurementRequest. Парсим строки
  // (включая price=0 — это «нет позиции у поставщика») и отдаём бэку одним
  // запросом. Бэк создаёт квоты для price>0 и REFUSED для price=0.
  if (linkedFileCode) {
    const rows: {
      partNumber: string
      brand: string | null
      price: number
      currency: string
      note: string | null
    }[] = []
    ws.eachRow((row, rowNumber) => {
      if (rowNumber === 1) return
      const partNumber = readCellText(row.getCell(COL.partNumber))
      const brand = readCellText(row.getCell(COL.brand)) || null
      const priceRaw = readCellText(row.getCell(COL.price))
      const note = readCellText(row.getCell(COL.comment)) || null
      if (!partNumber || !priceRaw) return
      const price = parseFloat(priceRaw.replace(/\s/g, '').replace(',', '.'))
      if (isNaN(price) || price < 0) {
        // Цена в ячейке есть, но это не корректное число — диагностируем.
        invalidRows.push({ row: rowNumber, value: priceRaw })
        return
      }
      rows.push({ partNumber, brand, price, currency: readCurrency(row), note })
    })
    if (rows.length === 0)
      return {
        created: 0,
        skippedOrphans: 0,
        skippedNoMatch: 0,
        skippedDuplicates: 0,
        notFound: 0,
        unmatched: 0,
        invalidRows,
      }
    const result = await uploadProcurementResponse({ fileCode: linkedFileCode, rows })
    return {
      created: result.answered,
      skippedOrphans: 0,
      skippedNoMatch: 0,
      skippedDuplicates: result.skippedDuplicates,
      // refused = строки с ценой 0 → позиции «Не найдено».
      notFound: result.refused,
      // Позиции, удалённые вместе с заявкой — раньше пропадали из тоста молча.
      unmatched: result.unmatched,
      invalidRows,
    }
  }

  // Старый путь (обратная совместимость): файл без fileCode — парсер
  // работает как раньше. Цена 0 пропускается на парсе НЕ полностью —
  // строка доходит до бэка, квота создаётся технической, позиция у
  // закупщика становится «Не найдено». Режем только отрицательную цену.
  const rows: ParsedRow[] = []

  ws.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return
    const requestItemId = isBatch ? readCellText(row.getCell(COL.id)) : null
    const supplierName = readCellText(row.getCell(COL.supplier))
    const partNumber = readCellText(row.getCell(COL.partNumber))
    const brand = readCellText(row.getCell(COL.brand)) || null
    const priceRaw = readCellText(row.getCell(COL.price))
    const supplierComment = readCellText(row.getCell(COL.comment))

    if (!partNumber || !priceRaw || !supplierName) return

    const priceClean = priceRaw.replace(/\s/g, '').replace(',', '.')
    const price = parseFloat(priceClean)
    if (isNaN(price) || price < 0) {
      // Цена в ячейке есть, но это не корректное число — диагностируем.
      invalidRows.push({ row: rowNumber, value: priceRaw })
      return
    }

    rows.push({
      requestItemId,
      partNumber,
      brand,
      supplierName,
      price,
      currency: readCurrency(row),
      note: supplierComment,
    })
  })

  // Собираем payload для одного bulk-запроса — один HTTP вместо N×100.
  const updatedItemIds = new Set<string>()
  const payload: Parameters<typeof createProcurementResultsBulk>[0] = []
  let skippedNoMatch = 0

  // Строка + резолвленная позиция: нужны блоку привязки к открытому запросу,
  // чтобы разделить загрузку на upload-путь и bulk-остаток.
  const resolvedRows: { row: ParsedRow; targetItemId: string }[] = []

  for (const row of rows) {
    let targetItemId = row.requestItemId
    if (!targetItemId) {
      const match = items.find(
        (i) => i.partNumber.trim().toLowerCase() === row.partNumber.toLowerCase(),
      )
      if (!match) {
        skippedNoMatch += 1
        continue
      }
      targetItemId = match.requestItemId
    }

    const matchItem = items.find((i) => i.requestItemId === targetItemId)
    const sourceType: SourceType = MATRIX_MAP[matchItem?.matrix ?? ''] ?? 'CHINA'

    payload.push({
      requestItemId: targetItemId,
      supplierName: row.supplierName,
      price: row.price,
      currency: row.currency,
      sourceType,
      note: row.note || undefined,
    })
    resolvedRows.push({ row, targetItemId })
    updatedItemIds.add(targetItemId)
  }

  if (payload.length === 0) {
    return {
      created: 0,
      skippedOrphans: 0,
      skippedNoMatch,
      skippedDuplicates: 0,
      notFound: 0,
      unmatched: 0,
      invalidRows,
    }
  }

  // Файл без метки может быть потерявшим её ответом на «запрос поставщику»:
  // метку procurement-request:... стирают сторонние редакторы (WPS, Google
  // Sheets) и копирование содержимого в новую книгу. Кейс 15.07.2026 — 35 квот
  // Tanya легли мимо BOM, запрос целиком остался «Ждёт ответа». Если все
  // строки от одного поставщика и его открытый запрос накрывает большинство
  // позиций файла — предлагаем закупщику привязать ответ к запросу.
  const linked = await maybeLinkToOpenRequest(resolvedRows)
  const linkedItemIds = linked?.linkedItemIds ?? new Set<string>()
  const bulkPayload = linked ? payload.filter((p) => !linkedItemIds.has(p.requestItemId)) : payload

  // Лимит bulk — 500 квот за запрос. При больших файлах режем на чанки.
  const CHUNK = 500
  let created = linked?.answered ?? 0
  let skippedOrphans = 0
  let skippedDuplicates = linked?.skippedDuplicates ?? 0
  for (let i = 0; i < bulkPayload.length; i += CHUNK) {
    const chunk = bulkPayload.slice(i, i + CHUNK)
    const result = await createProcurementResultsBulk(chunk)
    created += result.created.length
    skippedOrphans += result.skippedOrphans ?? 0
    skippedDuplicates += result.skippedDuplicates ?? 0
  }

  // Перевод всех затронутых позиций в QUOTED — пока одиночные PATCH,
  // но их на порядок меньше, чем квот (каждая позиция одна, независимо
  // от количества квот по ней). Позиции, ушедшие в запрос (upload-путь),
  // не трогаем — бэк там сам делает автопереходы, как в новом пути парсера.
  await Promise.allSettled(
    [...updatedItemIds]
      .filter((id) => !linkedItemIds.has(id))
      .map((id) => updateItemStatus(id, 'QUOTED')),
  )

  // В старом пути квоты с ценой 0 создаются наравне с остальными и входят
  // в created — отдельным notFound их не считаем (в upload-пути привязки
  // они возвращаются как refused → notFound).
  return {
    created,
    skippedOrphans,
    skippedNoMatch,
    skippedDuplicates,
    notFound: linked?.refused ?? 0,
    unmatched: linked?.unmatched ?? 0,
    invalidRows,
  }
}

// Предложение привязки файла без метки к открытому «запросу поставщику».
// Возвращает результат upload-пути и набор привязанных позиций, либо null —
// кандидата нет, пересечение ниже порога, закупщик отказался или сам поиск
// упал (ошибка поиска не должна ломать обычную загрузку).
async function maybeLinkToOpenRequest(
  resolvedRows: { row: ParsedRow; targetItemId: string }[],
): Promise<{
  answered: number
  refused: number
  unmatched: number
  skippedDuplicates: number
  linkedItemIds: Set<string>
} | null> {
  if (resolvedRows.length === 0) return null
  const supplierKeys = new Set(resolvedRows.map((r) => r.row.supplierName.trim().toLowerCase()))
  if (supplierKeys.size !== 1) return null
  const supplierName = resolvedRows[0]!.row.supplierName.trim()
  const uniqueItemIds = Array.from(new Set(resolvedRows.map((r) => r.targetItemId)))

  let candidate
  try {
    candidate = await findProcurementRequestCandidate({
      supplierName,
      requestItemIds: uniqueItemIds,
    })
  } catch {
    return null
  }
  if (!candidate) return null
  if (candidate.matchedItems / uniqueItemIds.length < MATCH_THRESHOLD) return null

  const ok = await confirm({
    title: `Файл похож на ответ по запросу ${candidate.fileCode}`,
    message:
      `В файле нет служебной метки (её стирают сторонние редакторы Excel), но ` +
      `${candidate.matchedItems} из ${uniqueItemIds.length} позиций файла совпадают с открытым ` +
      `запросом ${candidate.fileCode} (${candidate.supplierName}). Привязать ответ к этому ` +
      `запросу? Совпавшие позиции получат в «Запросах поставщикам» статус «Ответил», ` +
      `остальные загрузятся обычными квотами.`,
    confirmText: 'Привязать к запросу',
    cancelText: 'Загрузить без привязки',
  })
  if (!ok) return null

  const linkedItemIds = new Set(candidate.matchedRequestItemIds)
  const uploadRows = resolvedRows
    .filter((r) => linkedItemIds.has(r.targetItemId))
    .map((r) => ({
      partNumber: r.row.partNumber,
      brand: r.row.brand,
      price: r.row.price,
      currency: r.row.currency,
      note: r.row.note || null,
    }))
  if (uploadRows.length === 0) return null

  const result = await uploadProcurementResponse({
    fileCode: candidate.fileCode,
    rows: uploadRows,
  })
  return {
    answered: result.answered,
    refused: result.refused,
    unmatched: result.unmatched,
    skippedDuplicates: result.skippedDuplicates,
    linkedItemIds,
  }
}
